import React, { useMemo } from "react"
import { Link, Repeater, Text, types, useAdminContext } from "react-bricks/frontend"

import { ButtonBrickProps } from "../../button/Button"
import { EqualHeightElement } from "react-equal-height/clean"
import { LazyImage } from "../../../shared/components/universal/image"
import { Product } from "../../../shared/sdk/types"
import classNames from "classnames"
import { useLocalesState } from "../../../shared/context/locales"
import { useMediaQuery } from "../../../hooks/use-media-query-admin"
import { useTranslation } from "react-i18next"
import { useUrlSearchParams } from "../../../shared/hooks/use-url-search-params"
import { RedesignButtonSize } from "../Button"
import { MatrixRedesignItemRowProps } from "./Row"
import { useCheckForEmptyText } from "../../../hooks/use-check-for-empty-text"
import { TypographyElement } from "../../RedesignTypography"
import MatrixRedesignItemPrice from "./Price"
import ConditionalWrapper from "../../../shared/components/miscellaneous/conditional-wrapper"
import { useQueryCart } from "../../../shared/hooks/cart/use-query-cart"

export interface MatrixRedesignItemProps {
    sku?: string
    firstOnMobile?: string
    index: number
    products?: Product[]
    highlight?: boolean
    realPrice?: boolean
    repeaterLength: number
    repeaterLengthFiltered: number
    changeTab: (sku?: string) => void
    activeTab: number
    showActiveOnly?: boolean
    repeater: {
        id: string
        props: MatrixRedesignItemRowProps
        type: "MatrixRedesignItemRow"
    }[]
    repeaterBottom: {
        id: string
        props: ButtonBrickProps
        type: "Button"
    }[]
    badge?: TypographyElement[]
    href: string
    target?: "_blank" | "_self"
}

const MatrixRedesignItem: types.Brick<MatrixRedesignItemProps> = ({
    sku,
    firstOnMobile,
    products,
    index,
    highlight,
    realPrice,
    repeaterLength,
    repeaterLengthFiltered,
    changeTab,
    activeTab,
    showActiveOnly,
    badge,
    href,
    target,
}) => {
    const { t } = useTranslation("translations")
    const { region, language } = useLocalesState()

    const product = products?.find(p => p.sku === sku)

    const { isAdmin } = useAdminContext()

    const isMobile = useMediaQuery("(max-width: 767px)", { initializeWithValue: false })
    const isIPadPro = useMediaQuery("(min-width: 1366px)", { initializeWithValue: false })

    const row = useMemo(
        () =>
            Math.ceil(
                (1 / (repeaterLengthFiltered % 3 === 0 || isIPadPro ? 3 : 2)) *
                    ((index >= repeaterLength - 1 ? index - (repeaterLength - repeaterLengthFiltered) : index) + 1)
            ),
        [repeaterLengthFiltered, repeaterLength, index, isIPadPro]
    )

    const active = useMemo(() => activeTab === index && !isIPadPro, [activeTab, index, isIPadPro])

    const [params] = useUrlSearchParams()

    const hl = useMemo(() => (params.hl ? params.hl === sku : highlight), [params, highlight, sku])

    const emptyBadge = useCheckForEmptyText(badge)

    const { data: cartData } = useQueryCart()

    const cartCode = useMemo(() => cartData?.voucher_trk, [cartData?.voucher_trk])

    if (sku && !product && !isAdmin) return null

    return (
        <li
            key={index}
            id={sku}
            className={classNames(
                "relative flex flex-col justify-end lg:col-span-2 lg:last:nth-[3n-1]:col-end-[-2] lg:nth-last-2:nth-[3n+1]:col-end-4",
                firstOnMobile === sku && "order-first lg:order-none"
            )}
        >
            {hl && !showActiveOnly && (
                <div className="flex">
                    <span className="text-box absolute top-0 left-1/2 z-2 block -translate-x-1/2 -translate-y-1/2 rounded-full bg-orange px-2.5 pt-1.5 pb-1 text-sm font-semibold whitespace-nowrap text-white xs:px-3 sm:px-3 lg:relative lg:left-15 lg:translate-x-0 lg:translate-y-0 lg:rounded-t-[18px] lg:rounded-b-[0] lg:px-6 lg:pt-2.5 lg:pb-[7px] lg:text-base xl:px-5">
                        {t(
                            product?.configuration.subscription
                                ? "product_matrix_highlight_subscription"
                                : isMobile
                                  ? "product_matrix_highlight"
                                  : "product_matrix_highlight_desktop"
                        )}
                    </span>
                </div>
            )}
            <div
                className={classNames(
                    "overflow-hidden rounded-[25px] xs:rounded-[30px] sm:rounded-xl lg:overflow-visible lg:rounded-3xl lg:border lg:border-border-tide lg:p-3.5 lg:pb-0",
                    !showActiveOnly && !active && "border border-border-tide",
                    !showActiveOnly && !active && hl && "border-2 border-orange lg:border-2 lg:border-orange",
                    !showActiveOnly && active && "shadow-black-base",
                    showActiveOnly && active && "pt-5"
                )}
            >
                <button
                    type="button"
                    onClick={() => changeTab(sku)}
                    className={classNames(
                        "relative block w-full pt-6 text-left xs:px-2.5 sm:px-3 sm:pt-5 md:pt-5.5 lg:block lg:rounded-[46px] lg:bg-background-tide lg:py-8 lg:pl-7 xl:pl-10",
                        active && "bg-[radial-gradient(circle_at_50%_50%,_#007dd1,_#00497a_82%)]",
                        hl && !active && "lg:bg-[linear-gradient(to_right,_#d07821,_#e7994c_74%,_#d07821)]",
                        !isAdmin && "lg:pointer-events-none",
                        showActiveOnly && "hidden"
                    )}
                    tabIndex={!!isIPadPro ? -1 : undefined}
                >
                    <EqualHeightElement name={"top-" + row}>
                        <div className="flex h-full flex-col justify-between lg:justify-center">
                            <div className="mb-3 sm:mb-3.5 lg:mb-0 lg:max-w-[calc(100%-107px)] xl:max-w-[calc(100%-147px)]">
                                <ConditionalWrapper
                                    condition={!!href && isIPadPro}
                                    wrapper={children => (
                                        <Link href={href} target={target} className="pointer-events-auto">
                                            {children}
                                        </Link>
                                    )}
                                >
                                    <EqualHeightElement name={"title-" + row}>
                                        <div className="flex h-full flex-col justify-center">
                                            <Text
                                                propName="name"
                                                placeholder="Lorem ipsum"
                                                renderBlock={({ children }) => (
                                                    <h3
                                                        className={classNames(
                                                            "mb-1 text-center font-gibson text-base/[18px] font-semibold sm:text-md lg:text-left lg:text-lg xl:text-xl",
                                                            {
                                                                "text-white": active,
                                                                "lg:text-white": hl,
                                                                "flex items-center": !!href && isIPadPro,
                                                            }
                                                        )}
                                                    >
                                                        <span className="inline-flex items-center lg:whitespace-nowrap">
                                                            {children}
                                                            {href && isIPadPro && (
                                                                <i className="lavita-icon ml-2 text-md xl:text-lg"></i>
                                                            )}
                                                        </span>
                                                    </h3>
                                                )}
                                            />
                                        </div>
                                    </EqualHeightElement>
                                </ConditionalWrapper>
                                <Text
                                    propName="description"
                                    renderBlock={({ children }) => (
                                        <p
                                            className={classNames(
                                                "hidden text-base lg:mr-5 lg:block lg:text-base xl:mr-0 xl:text-md",
                                                {
                                                    "text-white": active || hl,
                                                }
                                            )}
                                        >
                                            {children}
                                        </p>
                                    )}
                                    placeholder="Lorem ipsum"
                                />
                            </div>
                            <div className="mx-auto w-[50px] xs:w-[57px] sm:w-[62px] lg:absolute lg:right-10 lg:bottom-0 lg:w-[87px] xl:right-[50px] xl:w-[97px]">
                                <div className="aspect-w-4 aspect-h-5">
                                    <LazyImage
                                        path={
                                            product?.marketing.images
                                                .find(i => i.type === "shop")
                                                ?.href.replace(process.env.IMG_CDN_URL_ASSETS, "") || ""
                                        }
                                        srcSet={[
                                            { w: 50 },
                                            { w: 57 },
                                            { w: 62 },
                                            { w: 87 },
                                            { w: 97, default: true },
                                            { w: 100 },
                                            { w: 114 },
                                            { w: 124 },
                                            { w: 174 },
                                            { w: 194 },
                                        ]}
                                        sizes="(max-width: 389px) 50px, (max-width: 767px) 57px, (max-width: 1023px) 62px, (max-width: 1365px) 87px, 97px"
                                        className="block"
                                    />
                                </div>
                            </div>
                        </div>
                    </EqualHeightElement>
                </button>
                <div
                    className={classNames(
                        "mx-2.5 xs:mx-0 lg:block lg:px-3.5 lg:py-7 xl:px-6.25",
                        !(showActiveOnly && active) && "hidden"
                    )}
                >
                    <Text
                        propName="description"
                        renderBlock={({ children }) => (
                            <p className="-mx-2.5 mb-5 w-[calc(100%+20px)] rounded-full bg-background-tide py-2 text-center text-sm font-semibold xs:mx-0 xs:w-full sm:mb-7 sm:text-base lg:hidden">
                                {children}
                            </p>
                        )}
                        placeholder="Lorem ipsum"
                    />
                    <Repeater propName="repeater" itemProps={{ row }} />
                    {!!href && !isIPadPro && (
                        <Link
                            href={href}
                            target={target}
                            className="pointer-events-auto mt-5 block text-base font-semibold text-blue underline sm:mt-7 sm:text-md"
                        >
                            {t("product_matrix_more_info")}
                        </Link>
                    )}
                    <MatrixRedesignItemPrice row={row} product={product} realPrice={realPrice} />{" "}
                    <ConditionalWrapper
                        condition={!!isIPadPro}
                        wrapper={children => <EqualHeightElement name={"info-" + row}>{children}</EqualHeightElement>}
                    >
                        <div>
                            {!emptyBadge && (
                                <Text
                                    propName="badge"
                                    renderBlock={({ children }) => (
                                        <p className="mb-5 w-full rounded-full bg-background-tide px-5 py-2 text-center text-sm font-semibold sm:mb-7 sm:px-7 sm:text-base">
                                            {children}
                                        </p>
                                    )}
                                    placeholder="Lorem ipsum"
                                />
                            )}
                            <Repeater propName="infoRepeater" itemProps={{ product: product }} />
                        </div>
                    </ConditionalWrapper>
                    <Repeater
                        propName="bottomRepeater"
                        itemProps={{
                            fullWidth: true,
                            size: RedesignButtonSize.Small,
                            redesign: true,
                            ...(product?.sku === "LVKUR" && {
                                href: `${process.env.SHOP_URL}/cart?code_trk=${cartCode}`,
                            }),
                        }}
                    />
                    {["DE", "AT", "CH"].includes(region) && ["de", "en"].includes(language) && (
                        <p className="mt-3 text-center text-sm font-semibold text-swamp-green sm:mt-3.5 lg:mt-5">
                            {t("product_matrix_shipping")}
                        </p>
                    )}
                </div>
            </div>
        </li>
    )
}

MatrixRedesignItem.schema = {
    name: "MatrixRedesignItem",
    label: "Matrix Redesign Item",
    hideFromAddMenu: true,
    repeaterItems: [
        {
            name: "repeater",
            items: [
                {
                    type: "MatrixRedesignItemRow",
                    label: "Matrix Redesign Item Row",
                    min: 1,
                },
            ],
        },
        {
            name: "infoRepeater",
            items: [
                {
                    type: "MatrixRedesignItemInfoBox",
                    label: "Info Box",
                    max: 1,
                },
            ],
        },
        {
            name: "bottomRepeater",
            items: [
                {
                    type: "Button",
                    label: "Button",
                    min: 1,
                },
            ],
        },
    ],
    sideEditProps: [
        {
            name: "sku",
            label: "SKU",
            type: types.SideEditPropType.Text,
        },
        {
            name: "highlight",
            label: "Highlight",
            type: types.SideEditPropType.Boolean,
        },
        {
            name: "realPrice",
            label: "Real Price",
            type: types.SideEditPropType.Boolean,
        },
        {
            name: "href",
            label: "href",
            type: types.SideEditPropType.Text,
        },
        {
            name: "target",
            label: "Target",
            type: types.SideEditPropType.Select,
            selectOptions: {
                options: [
                    {
                        value: "_self",
                        label: "Self",
                    },
                    {
                        value: "_blank",
                        label: "Blank",
                    },
                ],
                display: types.OptionsDisplay.Select,
            },
        },
    ],
}

export default MatrixRedesignItem
